"use client";

import React, { useState } from "react";
import { CircleCheck as CheckCircle2, CircleAlert as AlertCircle, Loader as Loader2 } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
interface FormDataState {
  name: string;
  phone: string;
  email: string;
  address: string;
  project_type: string;
  details: string;
  contact_method: string;
  timing: string;
  budget: string;
  consent: boolean;
}
const initialFormState: FormDataState = {
  name: "",
  phone: "",
  email: "",
  address: "",
  project_type: "",
  details: "",
  contact_method: "",
  timing: "",
  budget: "",
  consent: false
};
const PROJECT_TYPE_OPTIONS = ["Kitchen Remodeling", "Bathroom Remodeling", "Whole-Home Remodeling", "Home Additions", "Basement Remodeling", "Interior Remodeling", "Outdoor Living", "Deck Remodeling", "Other"];
const CONTACT_METHOD_OPTIONS = ["Phone", "Text", "Email"];
const TIMING_OPTIONS = ["ASAP", "Within 1 month", "1-3 months", "3-6 months", "Just researching"];
const BUDGET_OPTIONS = ["Under $25,000", "$25,000-$50,000", "$50,000-$100,000", "$100,000+", "Not sure yet"];
export default function RequestEstimate() {
  const [formData, setFormData] = useState<FormDataState>(initialFormState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrorMessage(null);
    if (!formData.consent) {
      setErrorMessage("Please accept the contact agreement before submitting.");
      return;
    }
    if (!formData.name || !formData.phone || !formData.email || !formData.address || !formData.project_type || !formData.contact_method || !formData.timing) {
      setErrorMessage("Please complete all required fields.");
      return;
    }
    setIsSubmitting(true);
    try {
      const params = new URLSearchParams();
      params.append("name", formData.name);
      params.append("phone", formData.phone);
      params.append("email", formData.email);
      params.append("address", formData.address);
      params.append("project_type", formData.project_type);
      params.append("details", formData.details);
      params.append("contact_method", formData.contact_method);
      params.append("timing", formData.timing);
      params.append("budget", formData.budget);
      params.append("website", "DecaturHomeRenovation.com");
      params.append("page", typeof window !== "undefined" ? window.location.pathname : "");
      params.append("lead_source", "organic");
      const response = await fetch("https://hooks.zapier.com/hooks/catch/28361868/4mcz4d7/", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded"
        },
        body: params.toString()
      });
      if (response.ok) {
        setIsSuccess(true);
      } else {
        setErrorMessage("Something went wrong. Please call (470) 847-9076 instead.");
      }
    } catch {
      setErrorMessage("Something went wrong. Please call (470) 847-9076 instead.");
    } finally {
      setIsSubmitting(false);
    }
  };
  return <section id="requestestimate" data-nav="light" className="bg-background text-foreground py-16 md:py-24 relative overflow-hidden">
      <div className="container mx-auto px-4 max-w-4xl relative z-10">
        <div className="text-center max-w-2xl mx-auto mb-10 md:mb-12">
          <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-3">
            Get Started
          </p>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-foreground mb-4">
            Request an Estimate
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            Tell us about your project goals. We will review your details and
            reach out to schedule an on-site consultation.
          </p>
        </div>

        <div className="bg-card text-card-foreground rounded-xl border border-border p-6 sm:p-8 md:p-10 shadow-md">
          {isSuccess ? <div className="py-12 px-4 text-center max-w-lg mx-auto">
              <div className="inline-flex items-center justify-center size-16 rounded-full bg-secondary text-foreground mb-6">
                <CheckCircle2 className="size-8" />
              </div>
              <h3 className="font-serif text-2xl sm:text-3xl font-semibold mb-3">
                Request Received
              </h3>
              <p className="text-muted-foreground text-base leading-relaxed mb-8">
                Thank you for reaching out. A renovation specialist will review
                your project details and contact you within one business day.
              </p>
              <Button type="button" variant="outline" onClick={() => {
            setFormData(initialFormState);
            setIsSuccess(false);
          }}>
                Submit another request
              </Button>
            </div> : <form onSubmit={handleSubmit} className="space-y-6">
              {errorMessage && <Alert variant="destructive">
                  <AlertCircle className="size-4" />
                  <AlertTitle>Notice</AlertTitle>
                  <AlertDescription>{errorMessage}</AlertDescription>
                </Alert>}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label htmlFor="name">
                    Full Name <span className="text-destructive">*</span>
                  </Label>
                  <Input id="name" name="name" type="text" required value={formData.name} onChange={e => setFormData({
                ...formData,
                name: e.target.value
              })} placeholder="Jane Doe" disabled={isSubmitting} />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">
                    Phone Number <span className="text-destructive">*</span>
                  </Label>
                  <Input id="phone" name="phone" type="tel" required value={formData.phone} onChange={e => setFormData({
                ...formData,
                phone: e.target.value
              })} placeholder="Your phone number" disabled={isSubmitting} />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label htmlFor="email">
                    Email Address <span className="text-destructive">*</span>
                  </Label>
                  <Input id="email" name="email" type="email" required placeholder="Your email address" value={formData.email} onChange={e => setFormData({
                ...formData,
                email: e.target.value
              })} disabled={isSubmitting} />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="address">
                    Street Address or ZIP Code{" "}
                    <span className="text-destructive">*</span>
                  </Label>
                  <Input id="address" name="address" type="text" required placeholder="e.g. 123 Church St, 30030" value={formData.address} onChange={e => setFormData({
                ...formData,
                address: e.target.value
              })} disabled={isSubmitting} />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label htmlFor="project_type">
                    Project Type <span className="text-destructive">*</span>
                  </Label>
                  <Select value={formData.project_type} onValueChange={val => setFormData({
                ...formData,
                project_type: val
              })} disabled={isSubmitting}>
                    <SelectTrigger id="project_type" className="w-full">
                      <SelectValue placeholder="Select a project type" />
                    </SelectTrigger>
                    <SelectContent>
                      {PROJECT_TYPE_OPTIONS.map((option, i) => <SelectItem key={option} value={option} data-index={i}>
                          {option}
                        </SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contact_method">
                    Preferred Contact Method{" "}
                    <span className="text-destructive">*</span>
                  </Label>
                  <Select value={formData.contact_method} onValueChange={val => setFormData({
                ...formData,
                contact_method: val
              })} disabled={isSubmitting}>
                    <SelectTrigger id="contact_method" className="w-full">
                      <SelectValue placeholder="Select method" />
                    </SelectTrigger>
                    <SelectContent>
                      {CONTACT_METHOD_OPTIONS.map((option, i) => <SelectItem key={option} value={option} data-index={i}>
                          {option}
                        </SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label htmlFor="timing">
                    Project Timing <span className="text-destructive">*</span>
                  </Label>
                  <Select value={formData.timing} onValueChange={val => setFormData({
                ...formData,
                timing: val
              })} disabled={isSubmitting}>
                    <SelectTrigger id="timing" className="w-full">
                      <SelectValue placeholder="Select timeframe" />
                    </SelectTrigger>
                    <SelectContent>
                      {TIMING_OPTIONS.map((option, i) => <SelectItem key={option} value={option} data-index={i}>
                          {option}
                        </SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="budget">Estimated Budget</Label>
                  <Select value={formData.budget} onValueChange={val => setFormData({
                ...formData,
                budget: val
              })} disabled={isSubmitting}>
                    <SelectTrigger id="budget" className="w-full">
                      <SelectValue placeholder="Select budget range (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      {BUDGET_OPTIONS.map((option, i) => <SelectItem key={option} value={option} data-index={i}>
                          {option}
                        </SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="details">Project Details</Label>
                <Textarea id="details" name="details" placeholder="Share any specific requirements, current conditions, or timeline notes..." className="min-h-28 resize-y" value={formData.details} onChange={e => setFormData({
              ...formData,
              details: e.target.value
            })} disabled={isSubmitting} />
              </div>

              <div className="pt-2">
                <div className="flex items-start gap-3">
                  <Checkbox id="consent" checked={formData.consent} onCheckedChange={checked => setFormData({
                ...formData,
                consent: checked === true
              })} disabled={isSubmitting} className="mt-1" />
                  <Label htmlFor="consent" className="text-xs sm:text-sm text-muted-foreground leading-normal font-normal cursor-pointer">
                    I agree to be contacted about my project by phone, text, or
                    email.
                  </Label>
                </div>
              </div>

              <div className="pt-4">
                <Button type="submit" size="lg" className="w-full sm:w-auto px-8 font-medium cursor-pointer" disabled={isSubmitting}>
                  {isSubmitting ? <>
                      <Loader2 className="size-4 animate-spin" />
                      Submitting...
                    </> : "Submit Request"}
                </Button>
              </div>
            </form>}
        </div>
      </div>
    </section>;
}